// Cache for standalone detection (only cache true values to avoid timing issues)
let standaloneCache = null;

// Detect if app is running in standalone/installed mode (cross-platform)
export const isStandalone = () => {
  // Return cached true value if available
  if (standaloneCache === true) {
    return true;
  }
  
  // iOS Safari
  if (window.navigator.standalone) {
    standaloneCache = true;
    return true;
  }
  
  // Other platforms (Android, Desktop PWA)
  // Check multiple ways to ensure we catch it even on initial launch
  if (window.matchMedia) {
    try {
      const standaloneMediaQuery = window.matchMedia('(display-mode: standalone)');
      if (standaloneMediaQuery && standaloneMediaQuery.matches) {
        standaloneCache = true;
        return true;
      }
    } catch (e) {
      // Media query not supported
    }
  }
  
  // Additional check: if window was opened without browser chrome, it's likely standalone
  // This helps catch cases where media query isn't ready yet
  if (window.matchMedia && typeof window.matchMedia('(display-mode: standalone)') === 'object') {
    // Media query API exists, try checking again after a microtask
    // This handles timing issues on initial launch
    setTimeout(() => {
      try {
        const standaloneMediaQuery = window.matchMedia('(display-mode: standalone)');
        if (standaloneMediaQuery && standaloneMediaQuery.matches && standaloneCache !== true) {
          standaloneCache = true;
          window.pwaInstalled = true;
          localStorage.setItem('pwaInstalled', 1);
        }
      } catch (e) {
        // Ignore
      }
    }, 0);
  }
  
  // Don't cache false - always re-check on next call to handle timing issues
  return false;
};

// Initialize PWA detection and event listeners
export const initPWA = () => {
  // Initialize deferred prompt
  window.deferredPrompt = null;

  // Re-check standalone status (fixes initial launch timing issues)
  const checkStandaloneAfterLoad = () => {
    const wasInstalled = window.pwaInstalled || localStorage.getItem('pwaInstalled');
    
    // iOS Safari
    if (window.navigator.standalone) {
      standaloneCache = true;
      window.pwaInstalled = true;
      localStorage.setItem('pwaInstalled', 1);
      return;
    }
    // Other platforms - check media query
    if (window.matchMedia) {
      try {
        const standaloneMediaQuery = window.matchMedia('(display-mode: standalone)');
        if (standaloneMediaQuery && standaloneMediaQuery.matches) {
          standaloneCache = true;
          window.pwaInstalled = true;
          localStorage.setItem('pwaInstalled', 1);
          return;
        }
      } catch (e) {
        // Media query not supported
      }
    }
    
    // Detect uninstall: if we were installed but are no longer in standalone mode
    // and we're not just viewing in a browser tab (check if we have a deferred prompt)
    if (wasInstalled && !isStandalone() && !window.deferredPrompt) {
      // App was likely uninstalled - clear the installed flag
      window.pwaInstalled = false;
      localStorage.removeItem('pwaInstalled');
      standaloneCache = false;
    }
  };
  
  // Check immediately (synchronous check)
  checkStandaloneAfterLoad();
  
  // Also check after DOM is ready and after page load (for timing issues)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkStandaloneAfterLoad);
  }
  window.addEventListener('load', checkStandaloneAfterLoad);
  
  // Check multiple times with small delays to catch initial launch timing issues
  setTimeout(checkStandaloneAfterLoad, 100);
  setTimeout(checkStandaloneAfterLoad, 500);
  
  // Check if PWA is installed (either currently in standalone mode, or previously installed)
  // Do this after the initial check to ensure we have the latest value
  window.pwaInstalled = isStandalone() || localStorage.getItem('pwaInstalled');

  // Listen for app installation
  window.addEventListener('appinstalled', () => {
    window.pwaInstalled = true;
    localStorage.setItem('pwaInstalled', 1);
  });

  // Listen for beforeinstallprompt event
  window.addEventListener('beforeinstallprompt', (e) => {
    // Check if we're actually in standalone mode (might not be detected yet on initial launch)
    const checkStandaloneNow = () => {
      if (window.navigator.standalone) {
        return true;
      }
      if (window.matchMedia) {
        try {
          return window.matchMedia('(display-mode: standalone)').matches;
        } catch (err) {
          return false;
        }
      }
      return false;
    };
    
    // Check immediately
    let actuallyStandalone = checkStandaloneNow();
    
    // If not detected immediately, check again after a short delay (handles initial launch timing)
    if (!actuallyStandalone && window.pwaInstalled) {
      // If localStorage says it's installed, give it a moment for media query to be ready
      setTimeout(() => {
        actuallyStandalone = checkStandaloneNow();
        if (actuallyStandalone) {
          standaloneCache = true;
          window.pwaInstalled = true;
          localStorage.setItem('pwaInstalled', 1);
          return;
        }
      }, 100);
    }
    
    // If we're actually in standalone mode OR localStorage says it's installed, ignore this event
    // (localStorage check handles initial launch timing issues)
    if (actuallyStandalone || window.pwaInstalled) {
      if (actuallyStandalone) {
        standaloneCache = true;
        window.pwaInstalled = true;
        localStorage.setItem('pwaInstalled', 1);
      }
      return;
    }
    
    e.preventDefault();
    window.deferredPrompt = e;
    // If beforeinstallprompt fires and we're not in standalone, the app is not installed yet
    window.pwaInstalled = false;
    localStorage.removeItem('pwaInstalled');
  });
};
